import { capabilitiesForRecording, type DriverInfo, type DriverState, type RaceEvent, type RaceRecording, type RaceSnapshot, type Telemetry, type Weather } from './race';
import { appendRecordingFacts, factKey } from './fact-ledger';

const emptyTelemetry = (): Telemetry => ({ speed: null, rpm: null, gear: null, throttle: null, brake: null, drs: 'UNKNOWN', x: null, y: null });
const emptyWeather = (): Weather => ({ airTemperature: null, trackTemperature: null, humidity: null, windSpeed: null, rainfall: null });

export function initialDriver(info: DriverInfo): DriverState {
  return { ...info, position: null, lap: 0, gapToLeader: null, interval: null, lastLap: null, bestLap: null,
    sectors: [null, null, null], compound: 'UNKNOWN', tyreAge: null, pitCount: 0, status: 'RUNNING', stints: [],
    telemetry: emptyTelemetry(), telemetryAtMs: null, locationAtMs: null, speedHistory: [] };
}

/** Later arrivals replace the same source identity, including corrections to the event's time. */
export function normalizeRecording(recording: RaceRecording): RaceRecording {
  const byId = new Map<string, RaceEvent>();
  for (const event of recording.events) {
    if (!Number.isFinite(event.atMs) || event.atMs < 0) continue;
    byId.set(factKey(event), event);
  }
  const normalized = { ...recording, events: [...byId.values()].sort((a, b) => a.atMs - b.atMs), durationMs: Math.max(0, recording.durationMs) };
  return { ...normalized, capabilities: capabilitiesForRecording(normalized) };
}

/** Input must be normalized. This pure projection is shared by replay and the live display buffer. */
export function buildSnapshot(recording: RaceRecording, elapsedMs: number): RaceSnapshot {
  const atMs = Math.max(0, Math.min(recording.durationMs, Number.isFinite(elapsedMs) ? elapsedMs : 0));
  const drivers = new Map(recording.drivers.map(info => [info.number, initialDriver(info)]));
  const session = { ...recording.session };
  const messages = new Map<string, Extract<RaceEvent, { type: 'message' }>['message']>();
  const pits = new Map<string, Extract<RaceEvent, { type: 'pit' }>['stop']>();
  let weather = emptyWeather();
  let trackStatus: RaceSnapshot['trackStatus'] = 'UNKNOWN';
  let qualifyingPhase: RaceSnapshot['qualifyingPhase'] = null;
  for (const event of recording.events) {
    if (event.atMs > atMs) break;
    if (event.type === 'timing') {
      const driver = drivers.get(event.driverNumber);
      if (driver) {
        const sectors = event.patch.sectors
          ? driver.sectors.map((value, index) => event.patch.sectors?.[index] ?? value) as DriverState['sectors']
          : undefined;
        Object.assign(driver, event.patch, sectors ? { sectors } : {}, event.patch.stints ? { stints: event.patch.stints.map(stint => ({ ...stint })) } : {});
      }
    } else if (event.type === 'telemetry') {
      const driver = drivers.get(event.driverNumber);
      if (!driver) continue;
      Object.assign(driver.telemetry, event.patch);
      if (event.patch.speed !== undefined || event.patch.rpm !== undefined || event.patch.throttle !== undefined || event.patch.brake !== undefined || event.patch.gear !== undefined || event.patch.drs !== undefined) driver.telemetryAtMs = event.atMs;
      if (event.patch.x !== undefined || event.patch.y !== undefined) driver.locationAtMs = event.atMs;
      if (event.atMs >= atMs - 30_000 && typeof event.patch.speed === 'number') driver.speedHistory.push({ atMs: event.atMs, speed: event.patch.speed });
    } else if (event.type === 'weather') weather = { ...event.weather };
    else if (event.type === 'status') trackStatus = event.status;
    else if (event.type === 'phase') qualifyingPhase = event.phase;
    else if (event.type === 'session') Object.assign(session, event.patch);
    else if (event.type === 'message') messages.set(event.message.id, { ...event.message });
    else if (event.type === 'pit') pits.set(event.stop.id, { ...event.stop });
  }
  const sorted = [...drivers.values()].sort((a, b) => (a.position ?? Infinity) - (b.position ?? Infinity) || a.number - b.number);
  return { atMs, session, drivers: sorted, messages: [...messages.values()].sort((a, b) => b.atMs - a.atMs),
    pitStops: [...pits.values()].sort((a, b) => b.atMs - a.atMs), weather, trackStatus, qualifyingPhase,
    lap: Math.max(0, ...sorted.map(driver => driver.lap)), track: recording.track, coverage: recording.coverage, capabilities: capabilitiesForRecording(recording) };
}

/** Keep the delay window plus each driver's last state before it. Long live sessions stay bounded. */
export interface LiveMergeOptions {
  /** Keep the complete session ledger for review and chart projections. */
  preserveFacts?: boolean;
  /** Retention window for transport snapshots. The durable server ledger is unaffected. */
  retentionMs?: number;
}

export function mergeLiveEvents(recording: RaceRecording, events: RaceEvent[], durationMs: number, options: LiveMergeOptions = {}): RaceRecording {
  const base = options.preserveFacts ? appendRecordingFacts(recording, events) : normalizeRecording({ ...recording, events: [...recording.events, ...events] });
  const normalized = { ...base, durationMs: Math.max(0, durationMs) };
  if (options.preserveFacts) return normalized;
  const cutoff = Math.max(0, durationMs - Math.max(1_000, options.retentionMs ?? 180_000));
  if (cutoff === 0) return normalized;
  const baseline = buildSnapshot(normalized, cutoff);
  const seed: RaceEvent[] = baseline.drivers.flatMap(driver => {
    const { number, code: _code, name: _name, team: _team, teamColor: _color, telemetry, telemetryAtMs, locationAtMs, speedHistory: _history, ...patch } = driver;
    const driverSeed: RaceEvent[] = [{ id: `seed:timing:${number}`, atMs: cutoff, type: 'timing', driverNumber: number, patch }];
    const { x, y, ...car } = telemetry;
    if (telemetryAtMs !== null) driverSeed.push({ id: `seed:car:${number}`, atMs: telemetryAtMs, type: 'telemetry', driverNumber: number, patch: car });
    if (locationAtMs !== null) driverSeed.push({ id: `seed:location:${number}`, atMs: locationAtMs, type: 'telemetry', driverNumber: number, patch: { x, y } });
    return driverSeed;
  });
  seed.push({ id: 'seed:weather', atMs: cutoff, type: 'weather', weather: baseline.weather }, { id: 'seed:status', atMs: cutoff, type: 'status', status: baseline.trackStatus },
    { id: 'seed:session', atMs: cutoff, type: 'session', patch: { totalLaps: baseline.session.totalLaps } },
    { id: 'seed:phase', atMs: cutoff, type: 'phase', phase: baseline.qualifyingPhase ?? null });
  baseline.messages.slice(0, 40).forEach(message => seed.push({ id: `seed:message:${message.id}`, atMs: message.atMs, type: 'message', message }));
  baseline.pitStops.slice(0, 40).forEach(stop => seed.push({ id: `seed:pit:${stop.id}`, atMs: stop.atMs, type: 'pit', stop }));
  const coverage = normalized.coverage ? {
    ...normalized.coverage,
    startMs: Math.max(cutoff, normalized.coverage.startMs),
    endMs: normalized.durationMs,
    missingStart: true,
    gaps: normalized.coverage.gaps.map(gap => ({ ...gap, fromMs: Math.max(cutoff, gap.fromMs), toMs: Math.min(normalized.durationMs, gap.toMs) })).filter(gap => gap.fromMs < gap.toMs),
  } : undefined;
  return normalizeRecording({ ...normalized, coverage, events: [...seed, ...normalized.events.filter(event => event.atMs > cutoff)] });
}

export function advanceClock(positionMs: number, elapsedMs: number, rate: number, durationMs: number): number {
  return Math.max(0, Math.min(durationMs, positionMs + Math.max(0, elapsedMs) * Math.max(0, rate)));
}

export function displayTime(positionMs: number, delaySeconds: number): number {
  return Math.max(0, positionMs - Math.max(0, Math.min(120, delaySeconds)) * 1000);
}
